import { Box, Grid2, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import CustomDivider from '../../shared/utils/CustomDivider'
import CourseCard from '../../shared/components/CourseCard'
import { useDialog } from '../../shared/hook/useDialog'
import MainDialogBox from '../../shared/components/Dialog/DialogMainBox'
import { LightButton } from '../../shared/utils/CustomButton'
import FilterListIcon from '@mui/icons-material/FilterList';
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { AutoCompleteField } from '../../shared/components/FormFields/AutoCompleteField'
import { DateFormField } from '../../shared/components/FormFields/DataFormField'
import DialogFooter from '../../shared/components/Dialog/DialogFooter'
import { Page_Padding } from '../../shared/constants'
import { useApartmentListForCourse, useCityListWithoutState, useCourseCategory, useCourses } from '../../assets/api/useGetApi'
import { SkeletonCom } from '../../shared/utils/SkeletonCom'
import useResponsive from '../../shared/utils/Responsive'
import { getGridSize } from '../../shared/formatter'
import { CustomMinimalDatePicker } from '../../shared/components/FormFields/CustomMinimalDatePicker'
import dayjs from 'dayjs';
import SelectBoxField from '../../shared/components/FormFields/SelectBoxField'
import { useParams } from 'react-router-dom'
import { useDebounce } from '../../shared/hook/useDebounce'



const CourseTypeOptions = [
    { id: 1, label: "Online", value: 1 },
    { id: 0, label: "Offline", value: 0 },
];

const CourseTypeOptionsSm = [
    { id: 1, label: "Online", value: "Online" },
    { id: 0, label: "Offline", value: "Offline" },
];

const Courses = ({ defaultCategory = "normal" }) => {

    const { category } = useParams();
    const courseType = category || defaultCategory;
    const [searchCity, setSearchCity] = useState("");

    const debouncedSearchCity = useDebounce(searchCity, 500);
    const { data: cityList, isLoading: isCityLoading } = useCityListWithoutState({ search: debouncedSearchCity });
    const [cityId, setCityId] = useState("");
    const [filters, setFilters] = useState({});
    const { open, handleClickOpen, handleClose } = useDialog();
    const isSmallScreen = useResponsive("sm");

    // Sentinel ref for infinite scroll
    const sentinelRef = useRef(null);

    const {
        flatData: allCourses,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useCourses({ course_type: courseType, ...filters });

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const { data: apartmentList, isLoading: isLoadingApartmentList } = useApartmentListForCourse({ city_id: cityId });
    const ApartmentOptions = apartmentList?.data?.map((item) => ({
        id: item?.id,
        label: item?.apartment_name,
        value: item?.apartment_name,
    })) || [];

    const { data: courseCategory, isLoading: isCategoryLoading } = useCourseCategory();
    const CategoryOptions = courseCategory?.data?.map((cat) => ({
        id: cat?.id,
        label: cat?.name,
        value: cat?.name,
    })) || [];

    const CityOptions = cityList?.data?.map((city) => ({
        id: city?.id,
        label: city?.city_name,
        value: city?.city_name,
    })) || [];

    const validationSchema = Yup.object().shape({});

    const handleSubmit = async (values, { resetForm }) => {
        // city is ui-only state, extract cityId separately
        const { city_id, ...rest } = values;
        const filteredValues = Object.fromEntries(
            Object.entries(rest).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
        );
        if (city_id) filteredValues.cityId = city_id;
        setFilters(filteredValues);
        handleClose();
        resetForm();
    };

    // Desktop inline filter handlers
    const handleChangeCategory = (value) => {
        setFilters(prev => ({ ...prev, category_id: value }));
    };
    const handleChangeApartment = (value) => {
        setFilters(prev => ({ ...prev, apartment_id: value }));
    };
    const handleChangeCity = (value) => {
        setCityId(value);
        setFilters(prev => ({ ...prev, apartment_id: "", cityId: value }));
    };
    const handleChangeCourseType = (value) => {
        setFilters(prev => ({ ...prev, online: value }));
    };
    const handleRemoveCourseType = () => {
        setFilters((prev) => {
            const next = { ...prev };
            delete next.online;
            return next;
        });
    };

    // Formik dialog initial values — kept in sync with current filters
    const dialogInitialValues = {
        start_date: filters?.start_date || "",
        end_date: filters?.end_date || "",
        category_id: filters?.category_id || "",
        city_id: filters?.cityId || "",
        apartment_id: filters?.apartment_id || "",
        online: filters?.online ?? "",
    };

    return (
        <Box className="body event_course" px={Page_Padding}>

            {/* Mobile: Filter button */}
            {isSmallScreen && (
                <Box textAlign={"end"}>
                    <LightButton onClick={handleClickOpen} text={"Filter"} icon={<FilterListIcon sx={{ color: "#000000", fontSize: "24px" }} />} />
                </Box>
            )}

            {/* Desktop: Inline filters */}
            {!isSmallScreen && (
                <Grid2 container spacing={"2rem"}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <CustomMinimalDatePicker
                            value={filters?.start_date ? dayjs(filters.start_date) : null}
                            onChange={(val) => {
                                setFilters(prev => ({
                                    ...prev,
                                    start_date: val ? val.format("YYYY-MM-DD") : ""
                                }));
                            }}
                            maxDate={filters?.end_date ? dayjs(filters.end_date) : null}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <CustomMinimalDatePicker
                            value={filters?.end_date ? dayjs(filters.end_date) : null}
                            onChange={(val) => {
                                setFilters(prev => ({
                                    ...prev,
                                    end_date: val ? val.format("YYYY-MM-DD") : ""
                                }));
                            }}
                            minDate={filters?.start_date ? dayjs(filters.start_date) : null}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <SelectBoxField
                            label="Select Category"
                            options={CategoryOptions}
                            isRequired={true}
                            onChange={handleChangeCategory}
                            isloading={isCategoryLoading}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <SelectBoxField
                            label="Select City"
                            options={CityOptions}
                            isRequired={true}
                            onChange={handleChangeCity}
                            isloading={isCityLoading}
                            isShow={false}
                            onSearchChange={(val) => setSearchCity(val)}
                            ClearFunc={() => setSearchCity("")}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <SelectBoxField
                            value={filters.apartment_id}
                            label="Select Apartment"
                            options={ApartmentOptions}
                            isRequired={true}
                            onChange={handleChangeApartment}
                            isloading={isLoadingApartmentList}
                            isShow={true}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <SelectBoxField
                            label="Select  Type"
                            options={CourseTypeOptions}
                            isRequired={true}
                            onChange={handleChangeCourseType}
                            ClearFunc={handleRemoveCourseType}
                        />
                    </Grid2>
                </Grid2>
            )}

            <CustomDivider orientation="horizontal" size={isSmallScreen ? "20px" : undefined} />

            {/* Course grid */}
            <Grid2 container spacing={5} sx={{ display: 'flex', justifyContent: "center" }}>
                {isLoading ? (
                    [...Array(9)].map((_, index) => (
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <SkeletonCom />
                        </Grid2>
                    ))
                ) : allCourses?.length === 0 ? (
                    <Box textAlign="center" width="100%">
                        <Typography variant='h6'>No Data Found.</Typography>
                    </Box>
                ) : (
                    allCourses.map((item, index) => (
                        <Grid2
                            size={{ xs: 12, sm: 6, md: getGridSize(allCourses?.length) }}
                            key={index}
                            display={"flex"}
                            alignItems={"stretch"}
                        >
                            <CourseCard item={item} />
                        </Grid2>
                    ))
                )}
            </Grid2>

            {/* Infinite scroll sentinel */}
            <Box ref={sentinelRef} sx={{ height: "40px", mt: 2 }} />

            {/* Fetching next page skeletons */}
            {isFetchingNextPage && (
                <Grid2 container spacing={5} sx={{ display: "flex", justifyContent: "center" }}>
                    {[...Array(3)].map((_, index) => (
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <SkeletonCom />
                        </Grid2>
                    ))}
                </Grid2>
            )}

            {/* Mobile filter dialog — all fields in Formik */}
            <MainDialogBox open={open} handleClose={handleClose} width={"30%"} heading={"Filter"}>
                <Formik
                    initialValues={dialogInitialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => (
                        <Form>
                            <Grid2 container spacing={"30px"}>
                                {/* Date range */}
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Field
                                        name="start_date"
                                        component={DateFormField}
                                        label="From Date"
                                        maxDate={values.end_date || undefined}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Field
                                        name="end_date"
                                        component={DateFormField}
                                        label="To Date"
                                        minDate={values.start_date || undefined}
                                    />
                                </Grid2>

                                {/* Category */}
                                <Grid2 size={12}>
                                    <Field
                                        name="category_id"
                                        component={AutoCompleteField}
                                        options={CategoryOptions}
                                        label="Course Category"
                                        isLoading={isCategoryLoading}
                                    />
                                </Grid2>

                                {/* City — updates apartment list */}
                                <Grid2 size={12}>
                                    <Field
                                        name="city_id"
                                        component={AutoCompleteField}
                                        options={CityOptions}
                                        label="City"
                                        isLoading={isCityLoading}
                                        // onValueChange={(val) => {
                                        //     setCityId(val);
                                        //     setFieldValue("city_id", val);
                                        //     setFieldValue("apartment_id", "");
                                        // }}
                                        onSearchChange={(val) => {
                                            setSearchCity(val)
                                        }}
                                    />
                                </Grid2>

                                {/* Apartment */}
                                <Grid2 size={12}>
                                    <Field
                                        name="apartment_id"
                                        component={AutoCompleteField}
                                        options={ApartmentOptions}
                                        label="Apartment"
                                        isLoading={isLoadingApartmentList}
                                        nooptionText={"Currently Unavailable"}
                                        isShow={true}
                                    />
                                </Grid2>

                                {/* Course type */}
                                <Grid2 size={12}>
                                    <Field
                                        name="online"
                                        component={AutoCompleteField}
                                        options={CourseTypeOptionsSm}
                                        label="Select  Type"
                                        isStoredId={true}

                                    />
                                </Grid2>

                                <Grid2 size={12}>
                                    <DialogFooter
                                        handleClose={() => {
                                            setFilters({});
                                            setCityId("");
                                            handleClose();
                                        }}
                                        buttonName={"Filter"}
                                    />
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </MainDialogBox>
        </Box>
    );
};

export default Courses;